import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';

const emailFormSchema = z.object({
  email: z.email({ message: 'Please enter a valid email address' }).toLowerCase(),
});

type EmailForm = z.infer<typeof emailFormSchema>;

const FORM_ID = 'invite-user-form';

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEmail?: string;
  onInvite: (email: string) => void;
}

/**
 * Dialog for inviting someone who isn't yet on Wexler. Prefilled with `defaultEmail`
 * when opened, letting the user confirm or tweak the address before sending.
 */
export function InviteUserDialog({ open, onOpenChange, defaultEmail = '', onInvite }: InviteUserDialogProps) {
  const form = useForm<EmailForm>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: { email: '' },
  });

  useEffect(() => {
    if (open) form.reset({ email: defaultEmail.trim() });
  }, [open, defaultEmail, form]);

  const handleSubmit = form.handleSubmit((data) => {
    onInvite(data.email);
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite User to Wexler</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-gray-600">
              Enter the email address of the person you'd like to invite to join your team on Wexler.
            </p>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input {...field} placeholder="name@example.com" type="email" autoFocus />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form={FORM_ID} variant="primary" disabled={!form.watch('email')?.trim()}>
            Send Invitation
            <Icon className="lucide--send" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
